import { Fragment, useEffect, useRef, useState } from "react";
import { Button, Tooltip } from "antd";
import { BoldOutlined, UnorderedListOutlined } from "@ant-design/icons";

/* ------------------------------------------------------------------ */
/* Markdown <-> HTML conversion (limited: line breaks, "- " bullets, **bold**) */
/* ------------------------------------------------------------------ */

const escapeHtml = (s) =>
    String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

const inlineMdToHtml = (s) =>
    escapeHtml(s).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

export const markdownToHtml = (md) => {
    const lines = String(md ?? "").replace(/\r\n?/g, "\n").split("\n");
    let html = "";
    let inList = false;
    lines.forEach((line) => {
        const m = line.match(/^\s*[-*]\s+(.*)$/);
        if (m) {
            if (!inList) {
                html += "<ul>";
                inList = true;
            }
            html += `<li>${inlineMdToHtml(m[1]) || "<br>"}</li>`;
            return;
        }
        if (inList) {
            html += "</ul>";
            inList = false;
        }
        html += `<div>${line.trim() ? inlineMdToHtml(line) : "<br>"}</div>`;
    });
    if (inList) html += "</ul>";
    return html;
};

const isBoldEl = (el) => {
    const tag = el.tagName;
    if (tag === "B" || tag === "STRONG") return true;
    const fw = el.style?.fontWeight;
    return fw === "bold" || Number(fw) >= 600;
};

const wrapBold = (inner) => {
    if (!inner.trim()) return inner;
    const lead = inner.match(/^\s*/)[0];
    const trail = inner.match(/\s*$/)[0];
    return `${lead}**${inner.trim()}**${trail}`;
};

const inlineToMd = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent.replace(/\u00a0/g, " ");
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
    if (node.tagName === "BR") return "\n";
    const inner = Array.from(node.childNodes).map(inlineToMd).join("");
    return isBoldEl(node) ? wrapBold(inner) : inner;
};

const BLOCK_TAGS = new Set(["DIV", "P", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE"]);

const blockToLines = (root) => {
    const lines = [];
    let current = "";
    const flush = () => {
        if (current !== "") lines.push(current);
        current = "";
    };

    Array.from(root.childNodes).forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
            const tag = node.tagName;
            if (tag === "BR") {
                lines.push(current);
                current = "";
                return;
            }
            if (tag === "UL" || tag === "OL") {
                flush();
                node.querySelectorAll(":scope > li").forEach((li) => {
                    const text = inlineToMd(li).replace(/\n+/g, " ").trim();
                    lines.push(`- ${text}`);
                });
                return;
            }
            if (BLOCK_TAGS.has(tag)) {
                flush();
                lines.push(...blockToLines(node));
                return;
            }
        }
        current += inlineToMd(node);
    });
    if (current !== "" || lines.length === 0) lines.push(current);
    return lines;
};

export const htmlToMarkdown = (root) => {
    if (!root) return "";
    return blockToLines(root)
        .join("\n")
        .replace(/\*\*\*\*/g, "")
        .replace(/\s+$/, "");
};

/* ------------------------------------------------------------------ */
/* Form field                                                         */
/* ------------------------------------------------------------------ */

/**
 * Form-compatible rich text field. Shows formatted text (bold, bullets,
 * line breaks) to the user, but the form value (onChange) is always Markdown,
 * so it is saved to the DB as Markdown.
 */
export function MarkdownField({ value, onChange, disabled, placeholder, rows = 4 }) {
    const editorRef = useRef(null);
    const lastEmitted = useRef(null);
    const [isEmpty, setIsEmpty] = useState(!String(value ?? "").trim());

    // Sync external value (e.g. API response / form reset) into the editor.
    useEffect(() => {
        if (disabled || !editorRef.current) return;
        const md = String(value ?? "");
        if (md !== lastEmitted.current) {
            editorRef.current.innerHTML = md.trim() ? markdownToHtml(md) : "";
            lastEmitted.current = md;
            setIsEmpty(!md.trim());
        }
    }, [value, disabled]);

    const emit = () => {
        const md = htmlToMarkdown(editorRef.current);
        lastEmitted.current = md;
        setIsEmpty(!md.trim());
        onChange?.(md);
    };

    const exec = (command) => {
        editorRef.current?.focus();
        document.execCommand(command, false);
        emit();
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        document.execCommand("insertText", false, text);
    };

    if (disabled) {
        return (
            <div
                style={{
                    minHeight: 74,
                    padding: "4px 11px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    background: "rgba(0,0,0,0.04)",
                    color: "rgba(0,0,0,0.88)",
                }}
            >
                <MarkdownText text={value} fallback="-" />
            </div>
        );
    }
    return (
        <div
            style={{
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                background: "#fff",
            }}
        >
            <div
                style={{
                    display: "flex",
                    gap: 4,
                    padding: "4px 6px",
                    borderBottom: "1px solid #f0f0f0",
                }}
            >
                <Tooltip title="Bold (Ctrl+B)">
                    <Button
                        size="small"
                        type="text"
                        icon={<BoldOutlined />}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => exec("bold")}
                    />
                </Tooltip>
                <Tooltip title="Bullet list">
                    <Button
                        size="small"
                        type="text"
                        icon={<UnorderedListOutlined />}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => exec("insertUnorderedList")}
                    />
                </Tooltip>
            </div>
            <div style={{ position: "relative" }}>
                {isEmpty && (
                    <div
                        style={{
                            position: "absolute",
                            top: 6,
                            left: 11,
                            color: "rgba(0,0,0,0.25)",
                            pointerEvents: "none",
                        }}
                    >
                        {placeholder}
                    </div>
                )}
                <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={emit}
                    onBlur={emit}
                    onPaste={handlePaste}
                    className="trackon-md-editor"
                    style={{
                        minHeight: rows * 22 + 12,
                        maxHeight: 320,
                        overflowY: "auto",
                        padding: "6px 11px",
                        outline: "none",
                        lineHeight: 1.6,
                        wordBreak: "break-word",
                    }}
                />
            </div>
            <style>{`.trackon-md-editor ul{margin:2px 0;padding-left:20px;list-style:disc}`}</style>
        </div>
    );
}

/**
 * Lightweight renderer for the limited Markdown returned by the API for
 * scopeOfWork / technologyRequirements:
 *  - line breaks are preserved
 *  - lines starting with "- " (or "* ") are rendered as bullet list items
 *  - **text** is rendered as bold
 * No HTML is injected, so content is safe to render.
 */
const renderInline = (text, keyPrefix) =>
    String(text)
        .split(/(\*\*[^*]+\*\*)/g)
        .filter((part) => part !== "")
        .map((part, idx) =>
            /^\*\*[^*]+\*\*$/.test(part) ? (
                <strong key={`${keyPrefix}-b-${idx}`}>{part.slice(2, -2)}</strong>
            ) : (
                <Fragment key={`${keyPrefix}-t-${idx}`}>{part}</Fragment>
            )
        );

const BULLET_REGEX = /^\s*[-*]\s+(.*)$/;

export default function MarkdownText({ text, fallback = "N/A", style }) {
    const value = text === undefined || text === null ? "" : String(text);
    if (!value.trim()) return <span style={style}>{fallback}</span>;

    const lines = value.replace(/\r\n?/g, "\n").split("\n");
    const blocks = [];
    let bullets = [];

    const flushBullets = () => {
        if (bullets.length === 0) return;
        const items = bullets;
        bullets = [];
        blocks.push(
            <ul
                key={`ul-${blocks.length}`}
                style={{ margin: "2px 0", paddingLeft: 20, listStyleType: "disc" }}
            >
                {items.map((item, i) => (
                    <li key={i} style={{ margin: "2px 0" }}>
                        {renderInline(item, `li-${blocks.length}-${i}`)}
                    </li>
                ))}
            </ul>
        );
    };

    lines.forEach((line, idx) => {
        const bulletMatch = line.match(BULLET_REGEX);
        if (bulletMatch) {
            bullets.push(bulletMatch[1]);
            return;
        }
        flushBullets();
        if (!line.trim()) {
            blocks.push(<div key={`sp-${idx}`} style={{ height: 8 }} />);
            return;
        }
        blocks.push(
            <div key={`p-${idx}`} style={{ margin: "2px 0" }}>
                {renderInline(line, `p-${idx}`)}
            </div>
        );
    });
    flushBullets();

    return (
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6, ...style }}>
            {blocks}
        </div>
    );
}
